import {
  useState,
  FocusEvent,
  ChangeEvent,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { ITopic, MessageType } from '@common/types';
import { Textarea } from '@components/ui/textarea';
import { QUESTION_QUERY_MIN_LENGTH } from '@constants/app';
import useChat from '@hooks/use-chat';
import { findFirstTopicInQuery, getExactTopicFromTopicsList } from '@lib/utils';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface IQuestionInputProps {
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  topics: ITopic[];
  addStaticMessage: (messages: string, type: MessageType) => void;
  setShowTopicsError: Dispatch<SetStateAction<boolean>>;
  sendQuery: (query: string, topicId: string) => Promise<void>;
  disabled: boolean;
}

const QUERY_PARAM = 'query';

function QuestionInput(props: IQuestionInputProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedTopic, setSelectedTopic } = useChat();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const initialQuery = searchParams.get(QUERY_PARAM);
    if (initialQuery && initialQuery.length > 0 && props.topics.length > 0) {
      props.setQuery(initialQuery);
      const updatedSearchParams = new URLSearchParams();
      updatedSearchParams.delete(QUERY_PARAM);
      setSearchParams(updatedSearchParams);
      handleSubmit(initialQuery);
    }
  }, [props.topics]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [props.query]);

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlurTextarea(e: FocusEvent<HTMLTextAreaElement>) {
    if (!e.target.value) {
      setIsFocused(false);
    }
  }

  function handleChangeTextarea(e: ChangeEvent<HTMLTextAreaElement>) {
    props.setQuery(e.target.value);
  }

  function handleEnter(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && props.query) {
      e.preventDefault();
      handleSubmit();
    }
  }

  function handleSubmit(initialQuery: string = '') {
    const queryTrimmed = initialQuery || props.query.trim();
    if (queryTrimmed === '') {
      return;
    }
    if (queryTrimmed.length < QUESTION_QUERY_MIN_LENGTH) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      props.addStaticMessage(
        t('chat.response.too_short', { count: QUESTION_QUERY_MIN_LENGTH }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }
    const queryTrimmedLowerCase = queryTrimmed.toLocaleLowerCase();
    const queryTrimmedUpperCase = queryTrimmed.toLocaleUpperCase();

    // If query is an exact topic
    const foundTopic = getExactTopicFromTopicsList(props.topics, queryTrimmedLowerCase);
    if (foundTopic) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      setSelectedTopic(foundTopic.id);
      props.addStaticMessage(
        t('chat.response.topic_changed', { topic: queryTrimmedUpperCase }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }

    const res = findFirstTopicInQuery(queryTrimmedLowerCase, props.topics);
    if (!res) {
      if (selectedTopic) {
        props.sendQuery(queryTrimmed, selectedTopic);
        return;
      } else {
        props.setShowTopicsError(true);
        return;
      }
    }
    setSelectedTopic(res!.id);
    props.sendQuery(queryTrimmed, res!.id);
    props.setShowTopicsError(false);
  }

  return (
    <div className="relative w-full mt-5">
      <label
        htmlFor="question-input"
        className={`absolute left-3 transition-all duration-200 ${
          isFocused || props.query
            ? `top-1 text-xs text-xircle-main ${props.disabled && '!text-slate-100'}`
            : 'top-1/2 -translate-y-1/2 text-gray-400'
        }`}
      >
        {t('chat.input.placeholder')}
      </label>
      <Textarea
        id="question-input"
        disabled={props.disabled}
        ref={textareaRef}
        value={props.query}
        onChange={handleChangeTextarea}
        onFocus={handleFocus}
        onBlur={handleBlurTextarea}
        onKeyDown={handleEnter}
        rows={1}
        className="pt-6 pb-2 px-3 pr-10 bg-white text-black min-h-14 text-base font-normal border-none resize-none overflow-hidden rounded-xl w-full focus:ring-0 focus:outline-none focus-visible:ring-0"
      />
      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
    </div>
  );
}

export default QuestionInput;
